import { HallidaySnapshot } from "../types";

import type { IframeLoadMonitor } from "./iframeLoadMonitor";

// ============================================
// Centralized Widget State
// ============================================

export type WidgetState = {
    // Preload state
    isPreloaded: boolean;
    iframe: HTMLIFrameElement | null;

    // Iframe load monitor
    iframeLoadMonitor: IframeLoadMonitor | null;

    // EMBED state
    resizeHandler: ((event: MessageEvent) => void) | null;
    lastKnownHeight: number | null;

    // Param tracking
    lastParamHash: string | null;
    lastMergedConfig: Record<string, unknown> | null;
    windowOrigin: string | null;

    // Deferred update: stored when params change before widget is ready
    pendingUpdateParams: { config: Record<string, unknown>; windowOrigin: string } | null;

    // Window manager state
    providerWindow: Window | null;
    messageHandlers: ((event: MessageEvent) => void)[];
};

const DEFAULT_STATE: WidgetState = {
    isPreloaded: false,
    iframe: null,
    iframeLoadMonitor: null,
    resizeHandler: null,
    lastKnownHeight: null,
    lastParamHash: null,
    lastMergedConfig: null,
    windowOrigin: null,
    pendingUpdateParams: null,
    providerWindow: null,
    messageHandlers: [],
};

// ============================================
// Per-instance DOM ids
// ============================================

/** Element ids owned by one widget instance. The default instance ("" key) keeps the legacy unsuffixed ids. */
export type WidgetDomIds = {
    iframe: string;
    wrapper: string;
    placeholder: string;
    skeleton: string;
};

const makeDomIds = (key: string): WidgetDomIds => {
    const suffix = key ? `-${key}` : "";
    return {
        iframe: `halliday-payments-widget${suffix}`,
        wrapper: `halliday-payments-wrapper${suffix}`,
        placeholder: `halliday-payments-placeholder${suffix}`,
        skeleton: `halliday-payments-skeleton${suffix}`,
    };
};

// ============================================
// @internal reactive store (useSyncExternalStore contract)
// ============================================
// Per-instance store backing the React bindings. `subscribe`/`getSnapshot` are @internal — the
// public surface observes the widget through events, `ready()`, and the state getters. `getSnapshot`
// returns a memoized object rebuilt ONLY on a real transition, so its reference identity is stable
// between unchanged reads (required by `useSyncExternalStore` to avoid render loops).

/** Per-instance reactive store: listener set + memoized snapshot. @internal */
export type ReactiveStore = {
    listeners: Set<() => void>;
    /** Memoized snapshot — replaced with a NEW object only on a real transition. */
    snapshot: HallidaySnapshot;
};

/** Shared, immutable default snapshot. Never mutated (setters always spread into a new object). */
const DEFAULT_SNAPSHOT: HallidaySnapshot = Object.freeze({
    isReady: false,
    isOpen: false,
    status: null,
    error: null,
    notifications: [],
});

const createStore = (): ReactiveStore => ({
    listeners: new Set(),
    snapshot: DEFAULT_SNAPSHOT,
});

// ============================================
// Instance context
// ============================================

/**
 * A single widget instance's state. Replaces the old module-level singleton: every engine function
 * receives a context explicitly so multiple instances never clobber each other's DOM, listeners, or
 * window references. `getContentWindow()` resolves to the iframe contentWindow per instance.
 */
export type WidgetContext = {
    /** Instance key; "" for the default instance → unsuffixed DOM ids. */
    readonly key: string;
    /** Per-instance DOM element ids, namespaced by `key`. */
    readonly ids: WidgetDomIds;
    /** Mutable per-instance state. */
    state: WidgetState;
    /** Latest-call-wins generation counter; bumped by each resolution-triggering entry point and by destroy(). */
    generation: number;
    /** @internal Per-instance reactive store (subscribe/getSnapshot). */
    store: ReactiveStore;
    /**
     * @internal Notified after the widget is hidden — both `close()` and a widget-initiated
     * `EVENT_WINDOW_CLOSE` flow through `hideWidget`. `HallidayPayments` wires this to emit its
     * `"close"` event; unset for the bare engine.
     */
    onClose?: (() => void) | undefined;
};

/** Create a fresh, isolated instance context. */
export const createWidgetState = (key = ""): WidgetContext => ({
    key,
    ids: makeDomIds(key),
    state: { ...DEFAULT_STATE, messageHandlers: [] },
    generation: 0,
    store: createStore(),
});

// ============================================
// Generation (latest-call-wins guard)
// ============================================

export const getGeneration = (ctx: WidgetContext): number => ctx.generation;

export const bumpGeneration = (ctx: WidgetContext): number => ++ctx.generation;

// ============================================
// Core Getters
// ============================================

export const getState = (ctx: WidgetContext): WidgetState => ctx.state;

export const isPreloaded = (ctx: WidgetContext): boolean => ctx.state.isPreloaded;

export const isInitialized = (ctx: WidgetContext): boolean =>
    ctx.state.iframe !== null;

export const getLastMergedConfig = (ctx: WidgetContext): Record<string, unknown> | null =>
    ctx.state.lastMergedConfig;

/** Resolves the active content window to this instance's iframe contentWindow. */
export const getContentWindow = (ctx: WidgetContext): Window | null =>
    ctx.state.iframe?.contentWindow ?? null;

export const getWindowOrigin = (ctx: WidgetContext): string | null => ctx.state.windowOrigin;

export const getProviderWindowRef = (ctx: WidgetContext) => ({
    get current() {
        return ctx.state.providerWindow;
    },
    set current(w: Window | null) {
        ctx.state.providerWindow = w;
    },
});

// ============================================
// Setters
// ============================================

export const setState = (partial: Partial<WidgetState>, ctx: WidgetContext): void => {
    ctx.state = { ...ctx.state, ...partial };
};

export const setLastParamHash = (hash: string, ctx: WidgetContext): void => {
    ctx.state.lastParamHash = hash;
};

// ============================================
// @internal reactive store accessors
// ============================================

const notify = (ctx: WidgetContext): void => {
    // Snapshot the listener set so an unsubscribe during iteration cannot skip a sibling.
    for (const listener of [...ctx.store.listeners]) listener();
};

/**
 * Subscribe to reactive-state transitions; returns an unsubscribe fn (useSyncExternalStore contract).
 * @internal
 */
export const subscribe = (listener: () => void, ctx: WidgetContext): (() => void) => {
    ctx.store.listeners.add(listener);
    return () => {
        ctx.store.listeners.delete(listener);
    };
};

/**
 * Cached, referentially-stable snapshot of reactive state. The returned object is replaced only on a
 * real transition, so repeated reads return the same reference until a field actually changes.
 * @internal
 */
export const getSnapshot = (ctx: WidgetContext): HallidaySnapshot => ctx.store.snapshot;

/**
 * Apply a reactive-state transition. Rebuilds the memoized snapshot and notifies listeners ONLY when
 * a field actually changes (reference inequality), keeping `getSnapshot` stable between unchanged
 * reads. `error` is cleared by passing `{ error: null }` on a new load cycle / entry-point call.
 * @internal
 */
export const setReactiveState = (
    partial: Partial<HallidaySnapshot>,
    ctx: WidgetContext,
): void => {
    const prev = ctx.store.snapshot;
    const changed = (Object.keys(partial) as (keyof HallidaySnapshot)[]).some((key) => prev[key] !== partial[key]);
    if (!changed) return;
    ctx.store.snapshot = { ...prev, ...partial };
    notify(ctx);
};

/**
 * Reset the reactive snapshot to defaults and notify (used by `resetAll`/`destroy`). Listeners are
 * preserved so a subscriber (e.g. React) stays attached if the instance is later reused. @internal
 */
export const resetStore = (ctx: WidgetContext): void => {
    if (ctx.store.snapshot === DEFAULT_SNAPSHOT) return; // already default → no transition
    ctx.store.snapshot = DEFAULT_SNAPSHOT;
    notify(ctx);
};

// ============================================
// Reset
// ============================================

export const resetAll = (ctx: WidgetContext): void => {
    ctx.state.iframeLoadMonitor?.destroy();
    ctx.state = { ...DEFAULT_STATE, messageHandlers: [] };
    // Destroy bumps the generation so any in-flight resolution is discarded before it can build/show.
    ctx.generation++;
    // Reactive snapshot returns to its initial values; subscribers are notified, but kept attached.
    resetStore(ctx);
};

// ============================================
// Param Serialization & Change Detection
// ============================================

export const serializeParams = (config: Record<string, unknown>): string => {
    const serializable = Object.entries(config)
        .filter(([, v]) => typeof v !== "function")
        .sort(([a], [b]) => a.localeCompare(b));
    return JSON.stringify(serializable);
};

export const hasParamsChanged = (newHash: string, ctx: WidgetContext): boolean => {
    return ctx.state.lastParamHash !== newHash;
};

export const mergeAndStoreConfig = (
    newParams: Record<string, unknown>,
    ctx: WidgetContext,
): Record<string, unknown> => {
    // Route through mergeParams so lastMergedConfig deletes-on-null and stays in lockstep with the
    // widget's post-merge state (a cleared key becomes absent, never stored as null).
    const merged = mergeParams(ctx.state.lastMergedConfig ?? {}, newParams);
    ctx.state.lastMergedConfig = merged;
    return merged;
};

// ============================================
// Pure Param Merge
// ============================================

/**
 * Shallow-merge `next` into `prev`. CONTRACT: an explicit `null` in `next` CLEARS the key — it is
 * DELETED from the result, not stored as `null`. `undefined` still overrides the value in place.
 * This is the single "null = clear" primitive shared by the SDK (`updateConfig`/`pipeline`) and the
 * widget's live-update path (`applyParamsUpdate`), so a cleared key becomes cleanly absent on both
 * sides — no `null` is ever stored. Pinned by the unit test in state.test.ts.
 */
export const mergeParams = <T extends Record<string, unknown>>(prev: T, next: Partial<T>): T => {
    const merged = { ...prev } as Record<string, unknown>;
    for (const key of Object.keys(next)) {
        const value = (next as Record<string, unknown>)[key];
        if (value === null) delete merged[key]; // null = clear signal
        else merged[key] = value;
    }
    return merged as T;
};

export const stripFunctions = (config: Record<string, unknown>): Record<string, unknown> =>
    Object.fromEntries(
        Object.entries(config)
            .filter(([, v]) => typeof v !== "function")
            .map(([k, v]) => [
                k,
                Array.isArray(v)
                    ? v
                          .filter((item) => typeof item !== "function")
                          .map((item) =>
                              item !== null && typeof item === "object"
                                  ? stripFunctions(item as Record<string, unknown>)
                                  : item,
                          )
                    : v !== null && typeof v === "object"
                      ? stripFunctions(v as Record<string, unknown>)
                      : v,
            ]),
    );
